import { motion } from "framer-motion";
import Lottie from "lottie-react";
import { Rocket, BookOpen, Award, TrendingUp } from "lucide-react";
import colleaguesAnimation from "../images/lottie/colleagues-lottie.json";
import { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const highlights = [
  { icon: <Rocket size={22} />, title: "Project-Based Learning", desc: "Build skills through assigned internship projects" },
  { icon: <BookOpen size={22} />, title: "Mentor Guidance", desc: "Guidance and feedback from designated mentors" },
  { icon: <Award size={22} />, title: "Performance-Based LOR", desc: "Considered for strong performance; not guaranteed" },
  { icon: <TrendingUp size={22} />, title: "Career Readiness", desc: "Build transferable skills for future opportunities" },
];

/* ultra smooth motion presets */
const smooth = { duration: 1.1, ease: [0.22, 1, 0.36, 1] };

const wordReveal = {
  hidden: { opacity: 0, y: 40, filter: "blur(8px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: smooth },
};

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const bubbleVariants = {
  initial: { opacity: 0, scale: 0.8, y: 20 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.85, y: -10 },
};

const chatFlow = [
  { doubt: "😕 Confused which domain to choose?", answer: "Explore project-based tracks at GYAN" },
  { doubt: "Looking to build practical experience?", answer: "Develop skills through assigned projects at GYAN" },
  { doubt: "Have theory but want to apply it?", answer: "Explore structured learning activities at GYAN" },
];

function ChatWithLottie() {
  const [index, setIndex] = useState(0);
  const [showDoubt, setShowDoubt] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setShowDoubt(true), 600);
    return () => clearTimeout(t1);
  }, [index]);

  useEffect(() => {
    if (!showDoubt) return;
    const t2 = setTimeout(() => setShowAnswer(true), 1000);
    return () => clearTimeout(t2);
  }, [showDoubt]);

  useEffect(() => {
    if (!showAnswer) return;
    const t3 = setTimeout(() => {
      setShowDoubt(false);
      setShowAnswer(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % chatFlow.length);
      }, 300);
    }, 2500);
    return () => clearTimeout(t3);
  }, [showAnswer]);

  const current = chatFlow[index];

  return (
    <div className="relative flex items-center justify-center">

      {/* DOUBT — WHITE */}
      <AnimatePresence mode="wait">
        {showDoubt && (
          <motion.div
            key={`doubt-${index}`}
            variants={bubbleVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.6 }}
            className="absolute -top-8 left-0
           bg-white text-black
           px-6 py-3.5
           rounded-2xl rounded-bl-sm
           text-base md:text-lg
           font-bold
           shadow-xl
           max-w-sm"

          >
            {current.doubt}
          </motion.div>
        )}
      </AnimatePresence>

      {/* LOTTIE CENTER */}
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 6, repeat: Infinity }}
        className="relative z-10 w-full max-w-xl"
      >
        <Lottie animationData={colleaguesAnimation} loop />
      </motion.div>

      {/* REPLY — GREEN */}
      <AnimatePresence mode="wait">
        {showAnswer && (
          <motion.div
            key={`answer-${index}`}
            variants={bubbleVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.6 }}
          className="absolute -bottom-14 right-0
           bg-green-500 text-white
           px-6 py-3.5
           rounded-2xl rounded-br-sm
           text-base md:text-lg
           font-bold
           shadow-xl
           max-w-sm"

          >
            {current.answer}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


export default function HeroSection() {
  return (
    <section
      className="relative overflow-hidden min-h-[96vh] flex items-center"
      style={{ fontFamily: "Figtree, sans-serif" }}
    >
      {/* background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#163a7a] via-[#1e4b9e] to-[#0c2252]" />

      {/* glow */}
      <motion.div
        animate={{ scale: [1, 1.08, 1] }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute top-1/3 left-1/4 w-[700px] h-[700px] bg-blue-500/20 blur-[140px] rounded-full"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">

        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* LEFT */}
          <motion.div variants={container} initial="hidden" animate="visible">

            <motion.div variants={wordReveal}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/20 mb-8">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-sm font-semibold text-white/80 uppercase tracking-wide">
                Applications Open 2026
              </span>
            </motion.div>

            <motion.h1 variants={wordReveal}
              className="text-7xl sm:text-8xl md:text-[110px] font-black leading-[0.85] tracking-tight">
              <span className="bg-gradient-to-r from-green-400 to-emerald-300 bg-clip-text text-transparent">
                GYAN
              </span>
            </motion.h1>

            <motion.p variants={wordReveal}
              className="mt-3 text-xl text-white/60 italic">
              — By AiM4u Software Solutions
            </motion.p>

            <motion.h2 variants={wordReveal}
              className="mt-10 text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight">
              Learn by Doing.
              <br />
              <span className="relative inline-block text-green-400">
                Grow by Building.
                <motion.span
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 1.2, delay: 1 }}
                  className="absolute -bottom-3 left-0 w-full h-1 bg-green-400 rounded origin-left"
                />
              </span>
            </motion.h2>

            <motion.p variants={wordReveal}
              className="mt-8 text-2xl text-white/70 max-w-xl leading-relaxed">
              Structured internships with project activities, learning,
              and opportunities to build career-ready skills.
            </motion.p>

            <motion.div variants={wordReveal}
              className="mt-12 flex gap-6">
              <Link to="/internship">
              <motion.button whileHover={{ scale: 1.06, y: -3 }}
                whileTap={{ scale: .97 }}
                className="px-12 py-5 text-xl font-bold rounded-xl bg-green-500 shadow-2xl">
                 Explore Programs→
              </motion.button>
              </Link>

            </motion.div>
          </motion.div>

          {/* RIGHT */}
          <motion.div
            initial={{ opacity: 0, scale: .85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ ...smooth, delay: .4 }}
            className="relative max-w-lg mx-auto">
            <ChatWithLottie />
          </motion.div>
        </div>

        {/* CIRCULAR 3D HIGHLIGHTS */}
        <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-10">
          {highlights.map((h, i) => (
            <motion.div
              key={h.title}
              initial={{ opacity: 0, y: 60, rotateX: 30 }}
              animate={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ ...smooth, delay: .8 + i * .18 }}
              whileHover={{ y: -12, scale: 1.12 }}
              className="flex flex-col items-center text-center">

             <motion.div
  animate={{
    y: [0, -8, 0],
    scale: [1, 1.05, 1],
  }}
  transition={{
    duration: 5 + i,
    repeat: Infinity,
    ease: "easeInOut",
  }}
  whileHover={{
    scale: 1.15,
    boxShadow:
      "0 40px 80px rgba(16,185,129,.5), 0 0 0 6px rgba(255,255,255,.15)",
  }}
  className="relative w-28 h-28 rounded-full
    bg-gradient-to-br from-green-400 to-emerald-600
    flex items-center justify-center text-white
    shadow-[0_30px_60px_rgba(0,0,0,.4)]
    ring-4 ring-white/20"
>
  <motion.div
    animate={{ rotate: 360 }}
    transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
    className="absolute inset-0 rounded-full border border-white/10"
  />
  {h.icon}
</motion.div>

              <p className="mt-5 text-xl font-bold text-white">{h.title}</p>
              <p className="text-sm text-white/60">{h.desc}</p>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
